import { useFormContext } from 'react-hook-form';
import { SearchableSelect } from '@/components/common';
import { useVehicleMakes } from '@/hooks/useVehicles';
import type { VehicleMake } from '@/services/vehicles';
import type { MotorQuoteFormData } from '../../schema';

/**
 * Make/Model/Variant are their own wizard steps (per the exact motor step
 * order), so — unlike the older QuoteRequestPage/QuoteCreatePage — this
 * doesn't embed the monolithic <VehicleSearch/> (which bundles its own
 * vehicle-type tabs + all three cascading selects into a single screen).
 * Instead each step reuses the same lookup hooks VehicleSearch is built on
 * (useVehicleMakes/Models/Variants), filtered by the category chosen in the
 * preceding Vehicle Type step.
 */
export function MakeStep() {
  const {
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<MotorQuoteFormData>();

  const vehicleCategory = watch('vehicle_category');
  const makeId = watch('make_id');
  const { data: makes, isLoading } = useVehicleMakes(vehicleCategory);

  const options = (makes ?? []).map((m: VehicleMake) => ({ value: m.id, label: m.name }));

  const handleChange = (id: string) => {
    const make = (makes ?? []).find((m: VehicleMake) => m.id === id);
    setValue('make_id', id, { shouldValidate: true, shouldDirty: true });
    setValue('make_name', make?.name ?? '', { shouldValidate: false });
    // Cascade reset: a new make invalidates any previously chosen model/variant.
    setValue('model_id', '', { shouldValidate: false });
    setValue('model_name', '', { shouldValidate: false });
    setValue('variant_id', '', { shouldValidate: false });
    setValue('variant_name', '', { shouldValidate: false });
    setValue('fuel_type', '', { shouldValidate: false });
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Make</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        Select the vehicle manufacturer.
      </p>
      <div className="sm:max-w-sm">
        <SearchableSelect
          id="make_id"
          label="Make"
          options={options}
          value={makeId}
          onChange={handleChange}
          placeholder={isLoading ? 'Loading...' : 'Select make'}
          error={errors.make_id?.message}
          required
        />
      </div>
    </div>
  );
}
